import { Box, CheckboxField, Flex, Typography } from '@iheartjane/reefer';
import { useCallback, useEffect } from 'react';

import { AgreementLabel } from './AgreementLabel';
import { AgreementLinks } from './AgreementLinks';

import styles from './MenuQuickCheckoutDrawer.module.css';

export type ConsentItem = {
  external_link: string | null;
  external_links: Array<{ label: string; url: string }>;
  id: number;
  label: string;
  required: boolean;
};

interface QuickCheckoutConsentProps {
  consentItems: ConsentItem[];
  consents: Record<string, boolean>;
  requiredConsentsValid: boolean;
  setConsents: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export const QuickCheckoutConsent = ({
  consentItems,
  consents,
  requiredConsentsValid,
  setConsents,
}: QuickCheckoutConsentProps) => {
  const handleConsentChange = useCallback(
    (id: number, checked: boolean) => {
      setConsents((prev) => ({ ...prev, [String(id)]: checked }));
    },
    [setConsents]
  );

  useEffect(() => {
    if (consentItems.length > 0) {
      setConsents((prev) => {
        const next: Record<string, boolean> = { ...prev };
        for (const item of consentItems) {
          if (!(String(item.id) in next)) {
            next[String(item.id)] = false;
          }
        }
        return next;
      });
    }
  }, [consentItems, setConsents]);

  if (consentItems.length === 0) return null;

  return (
    <Flex
      flexDirection="column"
      className={styles['checkoutAccordionItem']}
      py={40}
      gap={24}
    >
      {consentItems.map((item) => (
        <Box key={item.id}>
          <CheckboxField
            label={
              <AgreementLabel required={item.required} label={item.label} />
            }
            name={`consent-${item.id}`}
            checked={consents[String(item.id)] || false}
            onChange={(checked) => handleConsentChange(item.id, checked)}
            aria-describedby={
              item.external_link || item.external_links.length > 0
                ? `agreement-links-${item.id}`
                : undefined
            }
          />
          {(item.external_link || item.external_links.length > 0) && (
            <AgreementLinks
              id={`agreement-links-${item.id}`}
              agreementLabel={item.label}
              external_link={item.external_link}
              external_links={item.external_links}
            />
          )}
        </Box>
      ))}
      {!requiredConsentsValid && (
        <Typography color="error" mt={12}>
          Please agree to the required terms.
        </Typography>
      )}
    </Flex>
  );
};
